// Local, encrypted storage for a Pending Action (#1693, spec.md §6.2).
//
// When a deep link arrives with no active session, the normalized action is
// encrypted (Web Crypto AES-GCM) and kept in localStorage — with
// createdAt/expiresAt/nonce, as the spec requires — until the user signs
// in. resumePendingAction.js then decrypts it, sends the PLAINTEXT fields
// to the server (resolvePendingAction) for the real authorization check,
// and clears it either way so it is consumed at most once.
//
// The AES key itself is Keychain/Keystore-backed on native (via
// nativeSecureStorage.js, #1691) — never localStorage, which sits right next
// to the ciphertext it would be protecting and would give near-zero real
// protection (Lion's decision on PR #1725, #1693). On web there is no
// Keychain/Keystore to fall back to, so the key stays in localStorage there
// — an explicit, declared fallback (see getStoredKeyJwk/setStoredKeyJwk
// below), not a silent one.

import {isNativePlatform} from '../platform/runtime'
import {NativeSecureStorage} from '../platform/adapters/nativeSecureStorage'

const STORAGE_KEY = 'kravcalc.pendingAction.v1'
const CRYPTO_KEY_STORAGE_KEY = 'kravcalc.pendingAction.key.v1'

// A local storage-hygiene bound only — NOT the authoritative TTL. The
// server is the source of truth for expiry
// (config/pendingActionPolicy.ttlMinutes, see
// functions/deepLinks/resolvePendingAction.js) precisely so it can change
// without a client deploy (ticket requirement 4). This constant only keeps
// a very stale, never-resumed local record from lingering in storage
// forever, so it is kept deliberately generous.
const LOCAL_HYGIENE_TTL_MS = 24 * 60 * 60 * 1000 // 24h

function getCrypto() {
  return typeof window !== 'undefined' ? window.crypto : undefined
}

function bufferToBase64(buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

function base64ToBuffer(base64) {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

// Declared per-platform storage for the key's JWK — see the file header for
// why native and web fall to different places.
async function getStoredKeyJwk() {
  if (isNativePlatform()) {
    const {value} = await NativeSecureStorage.get({key: CRYPTO_KEY_STORAGE_KEY})
    return value ?? null
  }
  return window.localStorage.getItem(CRYPTO_KEY_STORAGE_KEY)
}

async function setStoredKeyJwk(jwkJson) {
  if (isNativePlatform()) {
    await NativeSecureStorage.set({key: CRYPTO_KEY_STORAGE_KEY, value: jwkJson})
    return
  }
  window.localStorage.setItem(CRYPTO_KEY_STORAGE_KEY, jwkJson)
}

async function getOrCreateKey() {
  const crypto = getCrypto()
  const subtle = crypto?.subtle
  if (!subtle) return null

  const stored = await getStoredKeyJwk()
  if (stored) {
    try {
      const jwk = JSON.parse(stored)
      return await subtle.importKey('jwk', jwk, {name: 'AES-GCM'}, true, ['encrypt', 'decrypt'])
    } catch {
      // Corrupt/unreadable — fall through and mint a fresh key. Any record
      // encrypted under the old key becomes unreadable too; readPendingAction()
      // already treats a decrypt failure as "no pending action".
    }
  }

  const key = await subtle.generateKey({name: 'AES-GCM', length: 256}, true, ['encrypt', 'decrypt'])
  const jwk = await subtle.exportKey('jwk', key)
  await setStoredKeyJwk(JSON.stringify(jwk))
  return key
}

function randomNonce() {
  const bytes = new Uint8Array(16)
  getCrypto().getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Encrypts and stores a normalized deep-link action (see
 * normalizeDeepLink.js), overwriting any previous one — only the most
 * recently tapped link is honored, matching "the app returns to the target
 * exactly once".
 * @param {{type: string, path: string, search: string}} action
 * @return {Promise<boolean>} false when Web Crypto is unavailable — the
 *   caller falls back to leaving the link unhandled rather than storing it
 *   unencrypted.
 */
export async function storePendingAction(action) {
  const crypto = getCrypto()
  const subtle = crypto?.subtle
  if (!subtle || !action) return false

  const key = await getOrCreateKey()
  if (!key) return false

  const createdAt = Date.now()
  const record = {
    ...action,
    createdAt,
    expiresAt: createdAt + LOCAL_HYGIENE_TTL_MS,
    nonce: randomNonce(),
  }

  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plaintext = new TextEncoder().encode(JSON.stringify(record))
  const ciphertext = await subtle.encrypt({name: 'AES-GCM', iv}, key, plaintext)

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
    iv: bufferToBase64(iv),
    data: bufferToBase64(ciphertext),
  }))
  return true
}

/**
 * Decrypts and returns the stored pending action, or `null` when there is
 * none, it failed to decrypt, or it is past the local hygiene bound. Does
 * NOT delete it — call clearPendingAction() once the caller is truly done
 * with it (see resumePendingAction.js), so a resolve that itself fails
 * (network error) can still be retried on the next app open instead of
 * being silently dropped.
 * @return {Promise<{type: string, path: string, search: string,
 *   createdAt: number, expiresAt: number, nonce: string}|null>}
 */
export async function readPendingAction() {
  const subtle = getCrypto()?.subtle
  if (!subtle) return null

  const raw = window.localStorage.getItem(STORAGE_KEY)
  if (!raw) return null

  let stored
  try {
    stored = JSON.parse(raw)
  } catch {
    return null
  }

  const key = await getOrCreateKey()
  if (!key) return null

  try {
    const iv = new Uint8Array(base64ToBuffer(stored.iv))
    const plaintext = await subtle.decrypt({name: 'AES-GCM', iv}, key, base64ToBuffer(stored.data))
    const record = JSON.parse(new TextDecoder().decode(plaintext))
    if (typeof record.expiresAt === 'number' && record.expiresAt < Date.now()) {
      return null
    }
    return record
  } catch {
    return null
  }
}

/** Deletes the stored pending action, if any. Idempotent. */
export function clearPendingAction() {
  window.localStorage.removeItem(STORAGE_KEY)
}
